/** RMS level (0–1) treated as non-silence for activity detection. */
const SPEECH_ACTIVITY_RMS_THRESHOLD = 0.02;
/** Continuous time above threshold required before speech is considered present. */
const SPEECH_ACTIVITY_REQUIRED_MS = 150;

export type SpeechActivityMonitor = {
  hasSpeech: () => boolean;
  stop: () => void;
};

function getAudioContextConstructor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (
    window.AudioContext ??
    (
      window as Window & {
        webkitAudioContext?: typeof AudioContext;
      }
    ).webkitAudioContext
  );
}

/**
 * Tracks whether the mic stream stays above an RMS threshold for
 * {@link SPEECH_ACTIVITY_REQUIRED_MS}. If Web Audio is unavailable, reports
 * speech as present so transcription is not blocked.
 */
export function createSpeechActivityMonitor(
  stream: MediaStream,
): SpeechActivityMonitor {
  const AudioContextCtor = getAudioContextConstructor();
  if (AudioContextCtor == null) {
    return {
      hasSpeech: () => true,
      stop: () => {},
    };
  }

  const audioContext = new AudioContextCtor();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.2;
  source.connect(analyser);

  const samples = new Float32Array(analyser.fftSize);
  let aboveThresholdSinceMs: number | null = null;
  let speechDetected = false;
  let rafId: number | null = null;
  let stopped = false;

  const tick = () => {
    if (stopped || speechDetected) {
      return;
    }

    analyser.getFloatTimeDomainData(samples);
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i += 1) {
      const sample = samples[i] ?? 0;
      sumSquares += sample * sample;
    }
    const rms = Math.sqrt(sumSquares / samples.length);

    if (rms >= SPEECH_ACTIVITY_RMS_THRESHOLD) {
      const now = performance.now();
      if (aboveThresholdSinceMs == null) {
        aboveThresholdSinceMs = now;
      } else if (now - aboveThresholdSinceMs >= SPEECH_ACTIVITY_REQUIRED_MS) {
        speechDetected = true;
        return;
      }
    } else {
      aboveThresholdSinceMs = null;
    }

    rafId = window.requestAnimationFrame(tick);
  };

  void audioContext.resume().then(() => {
    if (!stopped && !speechDetected) {
      rafId = window.requestAnimationFrame(tick);
    }
  });

  return {
    hasSpeech: () => speechDetected,
    stop: () => {
      if (stopped) {
        return;
      }
      stopped = true;
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        // Already disconnected.
      }
      void audioContext.close();
    },
  };
}
